import { useState, useEffect, useRef } from 'react';

const Captcha = ({ onVerify, theme = 'purple' }) => {
  const [captchaNumber, setCaptchaNumber] = useState('');
  const [userInputs, setUserInputs] = useState(['', '', '', '']);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Generate random 4-digit number
  const generateCaptcha = () => {
    const randomNum = Math.floor(1000 + Math.random() * 9000).toString();
    setCaptchaNumber(randomNum);
    setUserInputs(['', '', '', '']);
    setIsVerified(false);
    setError('');
  };

  // Initialize captcha on mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    generateCaptcha();
  };

  // Handle input change for individual digit
  const handleInputChange = (idx, value) => {
    // Only allow single digit
    if (!/^\d?$/.test(value)) return;

    const newInputs = [...userInputs];
    newInputs[idx] = value;
    setUserInputs(newInputs);
    setError('');

    // Auto-shift to next field
    if (value && idx < 3) {
      inputRefs[idx + 1].current?.focus();
    } else if (value && idx === 3) {
      // Auto-verify when last digit is entered
      setTimeout(() => {
        const userInput = newInputs.join('');
        if (userInput === captchaNumber) {
          setIsVerified(true);
          setError('');
          onVerify(true);
        } else {
          setIsVerified(false);
          setError('Incorrect CAPTCHA. Try again.');
          onVerify(false);
        }
      }, 100);
    }
  };

  // Handle backspace to go to previous field
  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !userInputs[idx] && idx > 0) {
      inputRefs[idx - 1].current?.focus();
    }
  };

  // Color theme mapping
  const colorMap = {
    purple: {
      border: '#E2E8F0',
      focus: '#5B4FCF',
      box: 'bg-white border-[#E2E8F0] focus:border-[#5B4FCF]',
      button: 'bg-gradient-to-r from-[#5B4FCF] to-[#7C6FE0] hover:shadow-lg hover:shadow-indigo-300/50',
      refreshIcon: 'text-[#5B4FCF]',
      success: 'text-green-600',
      danger: 'text-red-600',
    },
  };

  const colors = colorMap[theme];

  return (
    <div className="mt-4 p-2 border border-[#E2E8F0] rounded-lg bg-slate-50">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-slate-700">CAPTCHA</label>
        <button
          type="button"
          onClick={handleRefresh}
          className="p-0.5 rounded-md hover:bg-slate-200 transition-colors"
          title="Refresh CAPTCHA"
        >
          <svg
            className={`w-4 h-4 ${colors.refreshIcon}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Display Boxes and Input Boxes in One Row */}
      <div className="flex gap-2 items-center">
        {/* CAPTCHA Display Box */}
        <div className="px-2 flex items-center justify-center border-2 border-[#5B4FCF] rounded-lg bg-gradient-to-br from-[#5B4FCF] to-[#7C6FE0] shadow-md">
          <span className="font-bold text-2xl text-white tracking-widest">
            {captchaNumber}
          </span>
        </div>

        {/* User Input Boxes */}
        <div className="flex gap-1">
          {[0, 1, 2, 3].map((idx) => (
            <input
              key={idx}
              ref={inputRefs[idx]}
              type="text"
              inputMode="numeric"
              maxLength="1"
              value={userInputs[idx]}
              onChange={(e) => handleInputChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              placeholder="0"
              className={`w-9 h-9 flex items-center justify-center text-center border-2 rounded font-bold text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-300 ${colors.box} focus:shadow-[0_0_0_2px_rgba(91,79,207,0.1)]`}
            />
          ))}
        </div>

        {isVerified && (
        <div className="mt-1.5 flex items-center gap-1">
          <svg
            className={`w-3 h-3 ${colors.success}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span className={`text-xs ${colors.success}`}>Verified</span>
        </div>
      )}
      </div>

      {/* Status Messages */}
      {error && (
        <p className={`mt-1.5 text-xs ${colors.danger}`}>{error}</p>
      )}
    </div>
  );
};

export default Captcha;
